import { Fragment } from 'react';

import { isCertExpired } from '../utils/ClubUtils';

import ExclamationIcon from '@usas/ui-web/components/icons/ExclamationIcon';
import InGoodStandingIcon from '@usas/ui-web/components/icons/InGoodStandingIcon';
import { SORT_TYPES } from '@usas/ui-web/components/table/utils/TableConstants';
import { Icon, Table, Td, Th, Tr } from '@usas/ui-web/components/table/Table';

import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { DEFAULT_MAX_DATE } from '@usas/ui-web/utils/Constants';

import global from '@usas/ui-web/components/GlobalStyle.module.css';
import ReportListAsButton from '@usas/ui-web/components/embeddedReports/ReportListAsButton';

const InvalidCert = ({ cert }) => (
  <Fragment>
    <td className={global.ExpiredCert}>{cert.courseName} {cert.courseCode ? `(${cert.courseCode})` : ''}</td>
    <td className={global.ExpiredCert}>{cert.courseCompletionDate !== null && cert.coursePassed === true
      ? formatDate(cert.courseCompletionDate) === DEFAULT_MAX_DATE
        ? ' '
        : formatDate(cert.courseCompletionDate)
      : cert.courseCompletionDate === null && cert.courseRequired === false ? 'Not Required Yet' : ' '}</td>
    <td className={global.ExpiredCert}>
      {cert.courseExpirationDate !== null && cert.coursePassed === true
        ? formatDate(cert.courseExpirationDate) === DEFAULT_MAX_DATE
          ? `Never`
          : formatDate(cert.courseExpirationDate)
        : ' '
      }
    </td>
  </Fragment>
);

const ValidCert = ({ cert }) => (
  <Fragment>
    <td>{cert.courseName} {cert.courseCode ? `(${cert.courseCode})` : ''}</td>
    <td>{cert.courseCompletionDate !== null && cert.coursePassed === true ? formatDate(cert.courseCompletionDate) === DEFAULT_MAX_DATE
      ? ' '
      : formatDate(cert.courseCompletionDate)
      : cert.courseCompletionDate === null && cert.courseRequired === false ? 'Not Required Yet' : ' '}</td>
    <td>
      {cert.courseExpirationDate !== null && cert.coursePassed === true
        ? formatDate(cert.courseExpirationDate) === DEFAULT_MAX_DATE
          ? `Never`
          : formatDate(cert.courseExpirationDate)
        : ' '
      }
    </td>
  </Fragment>
);

const SmallNestedTable = ({ clubMember }) => (
  <Fragment>
    <Table.Nested>
      <tbody>
        <tr>
          <th>Email</th>
          <td>{clubMember.email}</td>
        </tr>
      </tbody>
    </Table.Nested>
    <Table.Nested>
      <thead>
        <tr>
          <th>Requirement</th>
          <th>Completed</th>
          <th>Expiration</th>
        </tr>
      </thead>
      <Table.NestedBody colSpan={3} data={clubMember.personRequirement} noDataText='No Requirements'
        renderRow={(cert, i) =>
          <tr key={i}>
            {isCertExpired(cert.courseExpirationDate) || (cert.courseCompletionDate === null && cert.courseRequired === true)
              ? <InvalidCert cert={cert} />
              : <ValidCert cert={cert} />
            }
          </tr>
        } />
    </Table.Nested>
  </Fragment>
);

const NestedTable = ({ data }) => (
  <Table.Nested>
    <thead>
      <tr>
        <th>Requirement</th>
        <th>Date Completed</th>
        <th>Expiration Date</th>
      </tr>
    </thead>
    <Table.NestedBody colSpan={3} data={data} noDataText='No Requirements'
      renderRow={(cert, i) =>
        <tr key={i}>
          {isCertExpired(cert.courseExpirationDate) || (cert.courseCompletionDate === null && cert.courseRequired === true)
            ? <InvalidCert cert={cert} />
            : <ValidCert cert={cert} />
          }
        </tr>
      } />
  </Table.Nested>
);

const ClubMemberRequirementsGrid = ({ data, isLoading, dataTotal, reportRouteName, orgUnitId }) => (
  <Table.ContextWrapper data={data} rowIdKey={'personRoleRequirementId'}>
    <Table.Large>
      <thead>
        <Tr.Top colSpan={5} showDataTotal={isLoading !== true} dataTotal={dataTotal} dataName='Members'>
          <ReportListAsButton
            buttonLabel='Print Report'
            routeName={reportRouteName}
            reportParameters={{ orgUnitId: orgUnitId }} />
        </Tr.Top>
        <tr>
          <Th.Sort sortKey={'lastName'} sortType={SORT_TYPES.ALPHABETICAL}>Name</Th.Sort>
          <Th.Sort sortKey={'orgRoleName'} sortType={SORT_TYPES.ALPHABETICAL}>Registration Role</Th.Sort>
          <Th.Sort sortKey={'email'} sortType={SORT_TYPES.ALPHABETICAL}>Email</Th.Sort>
          <Th.Sort sortKey={'isInGoodStanding'} sortType={SORT_TYPES.NUMERICAL}>In Good Standing</Th.Sort>
          <Th.Action>
            <Icon.ExpandAll />
          </Th.Action>
        </tr>
      </thead>
      <Table.Body colSpan={5} isLoading={isLoading} noDataText='No Member Requirements'
        renderRow={(clubMember, i) =>
          <Tr.Accordion key={i} rowId={clubMember.personRoleRequirementId} tableColSpan={5} nestedTable={<NestedTable data={clubMember.personRequirement} />}>
            <td>{clubMember.preferredName || clubMember.firstName} {clubMember.lastName}</td>
            <td>{clubMember.orgRoleName}</td>
            <td>{clubMember.email}</td>
            <td>{clubMember.isInGoodStanding === true ? <span style={{ marginLeft: '4px' }}><InGoodStandingIcon /> Yes</span> : <span className={global.ExpiredCert}><ExclamationIcon toolTipText='Not In Good Standing' /> No</span>}</td>
            <Td.Action>
              <Icon.Expand rowId={clubMember.personRoleRequirementId} />
            </Td.Action>
          </Tr.Accordion>
        } />
    </Table.Large>
    <Table.Small>
      <thead>
        <Tr.Top colSpan={5} showDataTotal={isLoading !== true} dataTotal={data.length} dataName='Members'>
          <ReportListAsButton
            buttonLabel='Print Report'
            routeName={reportRouteName}
            reportParameters={{ orgUnitId: orgUnitId }} />
        </Tr.Top>
        <tr>
          <Th.Sort sortKey={'lastName'} sortType={SORT_TYPES.ALPHABETICAL}>Name</Th.Sort>
          <Th.Sort sortKey={'orgRoleName'} sortType={SORT_TYPES.ALPHABETICAL}>Role</Th.Sort>
          <th>Standing</th>
          <Th.Action>
            <Icon.ExpandAll />
          </Th.Action>
        </tr>
      </thead>
      <Table.Body colSpan={4} isLoading={isLoading} noDataText='No Member Requirements'
        renderRow={(clubMember, i) =>
          <Tr.Accordion key={i} rowId={clubMember.personRoleRequirementId} tableColSpan={4} nestedTable={<SmallNestedTable clubMember={clubMember} />}>
            <td>{clubMember.preferredName || clubMember.firstName} {clubMember.lastName}</td>
            <td>{clubMember.orgRoleName}</td>
            <td>{clubMember.isInGoodStanding === true ? <span style={{ marginLeft: '4px' }}><InGoodStandingIcon /></span> : <span className={global.ExpiredCert}><ExclamationIcon toolTipText='Not In Good Standing' /></span>}</td>
            <Td.Action>
              <Icon.Expand rowId={clubMember.personRoleRequirementId} />
            </Td.Action>
          </Tr.Accordion>
        } />
    </Table.Small>
  </Table.ContextWrapper>
);

export default ClubMemberRequirementsGrid;